import React, { memo, useCallback, useMemo, useEffect, Fragment, useState } from 'react';
import { css } from '@emotion/react';
import { tap } from 'rxjs/operators';
import { ModalHeader, ModalContent, ModalFooter, ModalAction, ModalDrawer } from '@core/components/Modal';
import { useModalInstance } from '@core/components/ModalStack';
import {
  useFormConfig,
  formGroup,
  formControl,
  useFormState,
  Validators,
  FormControl,
  FormDataType,
  formArray,
  FormArray,
  FormGroup,
} from '@core/utils/form';
import { useBloc } from '@core/utils/bloc';
import { FormViewModalBloc } from './FormViewModalBloc';
import { FormValueHistoryResponse } from '@business/entities/form';
import { t } from 'ttag';
import { FCFormDataType, FCType } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/types';
import { R } from '@core/utils/r';
import {
  FCTextFormData,
  FCDateFormData,
  FCParagraphFormData,
  FCDropdownFormData,
  FCUploadFormData,
  FCPhoneFormData,
  FCCheckboxFormData,
  FCRadioFormData,
  FCDescriptionFormData,
  FCTitleFormData,
} from '@modules/settings/SettingsPage/Components/FormsTab/FormFields';
import {
  FormInput,
  FormDateInput,
  FormSelect,
  FormCheckboxGroup,
  FormCheckboxGruped,
  FormText,
  FormRadioMaterial,
  FormInputPhone,
} from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { FormUpload } from '@core/components/form/FormUpload';
import { CommonService, HTMLService } from '@business/services';
import { FormRadioGroup, FormRadio } from '@core/components/form/FormRadio';
import { Label, Media, CardTitle, Badge, Table, UncontrolledTooltip, Alert } from 'reactstrap';
import { FileFormResponse, FormShareResponse } from '@business/entities/file';
import { Formatter } from '@core/utils/formatter';
import { WithTooltip, HTMLErrorBoundary, AssistantButton } from '@modules/common';
import { openFormShareModal } from '../FormSharesModal';
import { UploadResponse, TenantIdentity } from '@business/entities/common';
import { ProcessStep } from './ProcessStep';
import { useObservable, useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useDidMount } from '@core/utils/hooks/react';
import { TextBlock } from '@modules/common/TextBlock';
import { toMoment } from '@core/utils/time';
import { DATE_FORMAT } from '@business/constants/time';
import { LibraryService } from '@business/services/library_service';
import { JOB_DESCRIPTION_AUTHORITIES, JobAuthorify, LibraryItemStatus, LibraryItemType } from '@business/entities/library';
import { SRLWrapper } from 'simple-react-lightbox';
import { AnimatedItems } from '@modules/public/PresentationPage/Components/AnimatedItems';
import { FormRepeatingStep } from './FormRepeatingStep';
import { FCRepeaterFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCRepeater';
import { useLazyEffect } from '@core/utils/react';
import { useCompany } from '@core/utils/hooks/misc';
import ReactPlayer from 'react-player';
import { MediaService } from '@business/services/media_service';
import { isTenants } from '@business/services/common_service/common_service';
import { FormGridView } from './FormGridView.tsx';
import { FormFunctionView } from './FormFunctionView';
import { defineGridForm, FCGridForm, FCGridFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCGrid';
import { defineFunctionForm, FCFunctionForm, FCFunctionFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCFunction';
import { Session } from '@modules/auth/session';
import { AppConfig } from '@core/utils/config';

export function defineCustomForm(fields: FCFormDataType[]) {
  return formGroup({
    ...R.fromPairs(fields.map((field, index) => [`${index}`, getFieldControl(field)])),
  });
}

export type CustomFormData = FormDataType<typeof defineCustomForm>;

function defineProcessForm() {
  return formGroup({
    content: formControl<string>({ initialValue: 'Your content here' }),
    type: formControl<string>({ initialValue: 'image' }),
    image: formControl<string>({}),
    videoUrl: formControl<string>({ label: 'Video Url' }),
    embedUrl: formControl<string>({ label: 'Video Url' }),
  });
}

export type ProcessFormType = ReturnType<typeof defineProcessForm>;
export type ProcessFormData = FormDataType<typeof defineProcessForm>;

function defineGridFormNew() {
  return formGroup({
    rows: formArray(() =>
      formGroup({
        cols: formArray(() =>
          formGroup({
            title: formControl<string>({ placeholder: 'Type here...' }),
          }),
        ),
      }),
    ),
  });
}

export type GridFormType = ReturnType<typeof defineGridFormNew>;

function defineFunctionFormNew() {
  return formGroup({
    function: formControl<string>({}),
    authorityLevel: formControl<JobAuthorify>({ placeholder: 'Authority Level', initialValue: JobAuthorify.getApproval }),
    isEdit: formControl<boolean>({ initialValue: true }),
  });
}

export type FunctionFormType = ReturnType<typeof defineFunctionFormNew>;

export function getFieldControl(field: FCFormDataType) {
  if (field.type === FCType.text) {
    const f = field as FCTextFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.min(f.minValueLength)],
    });
  } else if (field.type === FCType.date) {
    const f = field as FCDateFormData;
    return formControl<number>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.email) {
    const f = field as FCTextFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.email],
    });
  } else if (field.type === FCType.paragraph) {
    const f = field as FCParagraphFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      validators: [Validators.min(f.minValueLength)],
    });
  } else if (field.type === FCType.dropdown) {
    const f = field as FCDropdownFormData;
    if (f.addMultiple) {
      return formControl<string[]>({
        required: f.isRequired,
        label: f.title,
        placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
      });
    }
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.upload) {
    const f = field as FCUploadFormData;
    return formControl<UploadResponse>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.phone) {
    const f = field as FCPhoneFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
      placeholder: !R.isEmpty(f.placeholder) ? f.placeholder : undefined,
    });
  } else if (field.type === FCType.checkbox) {
    const f = field as FCCheckboxFormData;
    return formControl<string[]>({
      required: f.isRequired,
      label: f.title,
    });
  } else if (field.type === FCType.radio) {
    const f = field as FCRadioFormData;
    return formControl<string>({
      required: f.isRequired,
      label: f.title,
    });
  } else if (field.type === FCType.desciption) {
    return formControl<string>({});
  } else if (field.type === FCType.title) {
    return formControl<string>({});
  } else if (field.type === FCType.process) {
    return formArray(() => defineProcessForm());
  } else if (field.type === FCType.repeater) {
    return formArray(() => defineRepeaterForm((field as any).items));
  } else if (field.type === FCType.grid) {
    return defineGridFormNew();
  } else if (field.type === FCType.function) {
    return formArray(() => defineFunctionFormNew());
  }

  return formControl<string>({});
}

function defineRepeaterForm(fields: FCFormDataType[]): any {
  return formGroup({
    ...R.fromPairs(fields.map((field, index) => [`${index}`, getFieldControl(field)])),
  });
}
export function getFieldJSX(
  field: FCFormDataType,
  index: number,
  inControl: FormControl<any> | FormArray<any> | FormGroup<any>,
  options?: { disabled?: boolean; material?: boolean; endOfDay?: boolean; enableLaserConversion?: boolean },
  countryCode?: string,
  phoneWithCountryCode?: boolean,
) {
  const { disabled = false } = options || {};
  const control = inControl as FormControl<any>;
  if (field.type === FCType.text || field.type === FCType.email) {
    return <FormInput key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />;
  } else if (field.type === FCType.date) {
    return <FormDateInput key={index} control={control} disabled={disabled} description={R.get(field, 'description')} endOfDay={options?.endOfDay} />;
  } else if (field.type === FCType.paragraph) {
    const f = field as FCParagraphFormData;
    if (f.showTextEditor) {
      return <FormHTML control={control} disabled={disabled} description={R.get(field, 'description')} />;
    }
    return <FormInput type="textarea" key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />;
  } else if (field.type === FCType.dropdown) {
    const f = field as FCDropdownFormData;
    const options = f.options.split('\n');
    const optionItems = CommonService.selectItems(options, o => o);
    return (
      <FormSelect
        type={f.addMultiple ? 'multiple' : 'single'}
        control={control}
        items={optionItems}
        key={index}
        disabled={disabled}
        description={R.get(field, 'description')}
      />
    );
  } else if (field.type === FCType.upload) {
    return (
      <FormUpload
        key={index}
        control={control}
        type="single"
        autoUpload
        autoUploadKey={`forms`}
        disabled={disabled}
        description={R.get(field, 'description')}
      />
    );
  } else if (field.type === FCType.phone) {
    return phoneWithCountryCode ? (
      <FormInputPhone key={index} countryCode={'us'} control={control} disabled={disabled} />
    ) : (
      <FormInput key={index} control={control} disabled={disabled} description={R.get(field, 'description')} />
    );
  } else if (field.type === FCType.checkbox) {
    const f = field as FCCheckboxFormData;
    const items = f.options.split('\n');

    const description = R.get(field, 'description');
    const uid = inControl.uid.replace('form:', '');
    const toolip = (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip placement="top" target={`control-description-${uid}`}>
          {description}
        </UncontrolledTooltip>
      </Fragment>
    );

    return (
      <Fragment key={index}>
        <Label>{f.title}</Label>
        {!!description && toolip}
        <FormCheckboxGroup control={control} disabled={disabled}>
          {items.map((option, order) => {
            return <FormCheckboxGruped label={option} value={option} key={order} disabled={disabled} />;
          })}
        </FormCheckboxGroup>
      </Fragment>
    );
  } else if (field.type === FCType.radio) {
    const f = field as FCRadioFormData;
    const items = f.options.split('\n');

    const description = R.get(field, 'description');
    const uid = inControl.uid.replace('form:', '');

    const toolip = (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip placement="top" target={`control-description-${uid}`}>
          {description}
        </UncontrolledTooltip>
      </Fragment>
    );

    if (options && options.material) {
      return (
        <Fragment key={index}>
          <Label>{f.title}</Label>
          {!!description && toolip}
          <FormRadioGroup control={control}>
            <AnimatedItems direction="up">
              {items.map((option, index) => (
                <FormRadioMaterial label={option} value={option} key={index} disabled={disabled} />
              ))}
            </AnimatedItems>
          </FormRadioGroup>
        </Fragment>
      );
    }

    return (
      <Fragment key={index}>
        <Label>{f.title}</Label>
        {!!description && toolip}
        <FormRadioGroup control={control} disabled={disabled}>
          {items.map((option, order) => (
            <FormRadio label={option} value={option} key={order} disabled={disabled} />
          ))}
        </FormRadioGroup>
      </Fragment>
    );
  } else if (field.type === FCType.desciption) {
    const f = field as FCDescriptionFormData;
    return HTMLService.toReact(f.description);
  } else if (field.type === FCType.title) {
    const f = field as FCTitleFormData;
    return (
      <div className="mt-4">
        <CardTitle>{f.title}</CardTitle>
        <hr />
      </div>
    );
  } else if (field.type == FCType.process) {
    return <ProcessStep form={control as any as FormArray<any>} />;
  } else if (field.type == FCType.repeater) {
    return (
      <FormRepeatingStep
        form={control as any as FormArray<any>}
        field={field as any as FCRepeaterFormData}
        enableLaserConversion={options?.enableLaserConversion}
      />
    );
  } else if (field.type === FCType.grid) {
    return <FormGridView form={control as any as GridFormType} field={field as any as FCGridFormData} />;
  } else if (field.type === FCType.function) {
    return <FormFunctionView form={control as any as FormArray<FunctionFormType>} field={field as any as FCFunctionFormData} />;
  }

  return <Fragment></Fragment>;
}

const styles = {
  content: css`
    padding: 25px !important;
  `,

  title: css`
    .MuiTypography-root {
      height: 50px;
      align-items: center;
      position: relative;
      padding: 0 10px 0 20px;
    }
  `,
};

interface HistoryItemProps {
  history: FormValueHistoryResponse;
}
const HistoryItem = memo((props: HistoryItemProps) => {
  const { history } = props;
  const description = useMemo(() => {
    const prefix = history.type === 'created' ? 'Created by' : history.type === 'shared' ? 'Shared by' : 'Updated by';
    const description = `${prefix} ${CommonService.getFullName(history.user)}`;
    const email = (R.isArray(history.values) && R.first(history.values)) || '';
    return history.type === 'shared' ? description + ' with ' + email : description;
  }, [history]);

  return (
    <li className="event-list" style={{ paddingBottom: 10 }}>
      <div className="event-timeline-dot">
        <i className="bx bx-right-arrow-circle font-size-18" />
      </div>

      <Media>
        <div className="mr-3">
          <h6 className="font-size-12">
            {Formatter.formatTime(history.created)} <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ml-2" />
            <br />
            <span style={{ fontSize: 12 }} className="text-muted">
              {Formatter.formatTime(history.created, 'hh:mm a')}
            </span>
          </h6>
        </div>
        <Media body>
          <div>{description}</div>
        </Media>
      </Media>
    </li>
  );
});

export interface FormViewModalProps {
  title: string;
  fileForm: FileFormResponse;
  identity?: TenantIdentity;
}

export const FormViewModal = memo((props: FormViewModalProps) => {
  const { fileForm, identity } = props;
  const bloc = useBloc(FormViewModalBloc, identity);
  const form = useMemo(() => defineCustomForm(fileForm.fields), [fileForm.fields]);
  const formValue = useNonNilObservable(form.value$);
  const modal = useModalInstance();
  const [sharedItems, setSharedItems] = useState(fileForm.shares || []);

  const onSubmit = useCallback(
    (data: CustomFormData) => {
      return bloc.saveFormValues(fileForm.id, JSON.stringify(data), identity).pipe(tap(() => modal.close()));
    },
    [bloc, fileForm.id, identity, modal],
  );

  useEffect(() => {
    if (fileForm.values) {
      form.patchValue(JSON.parse(fileForm.values));
    }
  }, [fileForm.values, form]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  const onShareManaged = useCallback(
    (items: FormShareResponse[]) => {
      setSharedItems(items);
      bloc.reloadFile(fileForm.id).subscribe();
    },
    [bloc, fileForm.id],
  );

  return (
    <ModalDrawer maxWidth="lg">
      <ModalHeader
        title={props.title}
        titleStyle={{ flex: 'inherit' }}
        rightContent={
          <div>
            {sharedItems.length > 0 && (
              <div style={{ cursor: 'pointer' }} onClick={() => openFormShareModal({ items: sharedItems, onDone: onShareManaged })}>
                <WithTooltip
                  tooltip={
                    <div>
                      {fileForm.shares.map(i => (
                        <div key={i.id}>{i.email}</div>
                      ))}
                    </div>
                  }
                >
                  <Badge className="ml-2" color="success">
                    Shared Publically
                  </Badge>
                </WithTooltip>
              </div>
            )}
          </div>
        }
      />
      <ModalContent formContainer={{ onSubmit: form.submit }} customCss={styles.content}>
        {fileForm.fields.map((field, index) => getFieldJSX(field, index, form.controls[`${index}`]))}
        <hr />
        <ul className="verti-timeline list-unstyled">
          {fileForm.history.map(history => (
            <HistoryItem history={history} key={history.id} />
          ))}
        </ul>
      </ModalContent>
      <ModalFooter>
        {!!fileForm.fields.length && <ModalAction intent="primary" text={t`Save`} loading={submitting} autoClose={false} onClick={form.submit} />}
      </ModalFooter>
    </ModalDrawer>
  );
});

export interface LibraryDocumentModalProps {
  itemId: string;
  onClose?: () => void;
}

export const LibraryDocumentModal = memo((props: LibraryDocumentModalProps) => {
  const { itemId, onClose } = props;
  const bloc = useBloc(FormViewModalBloc);

  const file = useObservable(bloc.file$);
  const titleControl = useMemo(() => formControl<string>({ initialValue: file?.name }), [file]);
  const titleControlValue = useNonNilObservable(titleControl.value$);
  const statusControl = useMemo(() => formControl<LibraryItemStatus>({ initialValue: file ? file.status : LibraryItemStatus.draft }), [file]);
  const statusControlValue = useNonNilObservable(statusControl.value$);

  const form = useMemo(() => defineCustomForm(file?.fields || []), [file]);
  const formValue = useNonNilObservable(form.value$);
  const modal = useModalInstance();

  const [autosaved, setAutoSaved] = useState(false);
  const [canSave, setCanSave] = useState(false);

  useEffect(() => {
    if (formValue) {
      setCanSave(true);
    }
  }, [formValue]);

  useDidMount(() => {
    bloc
      .fetchFile(itemId)
      .pipe(tap(f => titleControl.setValue(f.name)))
      .subscribe();
  });

  const onSubmit = useCallback(
    (data: CustomFormData, autosave = false) => {
      return bloc
        .updateItem(
          itemId,
          {
            type: LibraryItemType.custom,
            name: titleControlValue || file!.name,
            status: statusControlValue || LibraryItemStatus.draft,
            content: data as any,
          },
          autosave,
        )
        .pipe(
          tap(() => setCanSave(false)),
          tap(() => !autosave && modal.close()),
          tap(() => !autosave && onClose && onClose()),
        );
    },
    [bloc, file, itemId, modal, onClose, statusControlValue, titleControlValue],
  );

  useEffect(() => {
    if (file && file.content) {
      form.patchValue(JSON.parse(file.content as any as string));
    }
  }, [file, form]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  useLazyEffect(
    () => {
      if (canSave) {
        onSubmit(formValue, true).subscribe();
        setAutoSaved(true);
        setTimeout(() => setAutoSaved(false), 3000);
      }
    },
    [formValue, canSave, onSubmit],
    10000,
  );

  const onAIOutput = useCallback(
    (output: string) => {
      // Check if the output is a valid JSON
      console.log('output', output);
      try {
        const result = JSON.parse(output);
        form.patchValue(result);
      } catch (e) {
        console.error('Invalid JSON output from AI:', e);
        return;
      }
    },
    [form],
  );

  const documentDataExplain = useMemo(() => {
    if (!file) return '';
    return (file.fields || [])
      .map((field, index) => {
        if (field.type === FCType.process) {
          return `"${index}" is a process steps in the following format:\n- "content": step content\n- "type": must be "text"\n- "image", "videoUrl", "embedUrl": leave as null`;
        } else if (field.type === FCType.paragraph || field.type === FCType.text) {
          return `"${index}" is for ${R.get(field, 'title')} field. Description: (${R.get(field, 'description')})`;
        } else if (field.type === FCType.function) {
          return `"${index}" is a list of functions. Each function has:\n- "function": a short task or role\n- "authorityLevel": one of "getApproval", "handle", or "act"\n- "isEdit": true`;
        } else if (field.type === FCType.title) {
          return `"${index}" is a UI title field and does not require any input`;
        }
      })
      .join('\n');
  }, [file]);

  function buildFormFormatFromFields(fields) {
    const formObj = {};
    fields.forEach((field, index) => {
      const type = field.type;
      if (type === 'process') {
        formObj[index] = [
          {
            content: '<div>Step content here</div>',
            type: 'text',
            image: null,
            videoUrl: null,
            embedUrl: null,
          },
        ];
      } else if (type === 'paragraph') {
        if (field.showTextEditor) {
          formObj[index] = `<div>Value for ${field.title}</div>`;
        } else {
          formObj[index] = `Value for ${field.title}`;
        }
      } else if (type === 'function') {
        formObj[index] = [
          {
            function: 'Describe the function here',
            authorityLevel: JobAuthorify.getApproval || JobAuthorify.handle || JobAuthorify.act,
            isEdit: true,
          },
        ];
      } else {
        formObj[index] = `Value for ${field.title}`;
      }
    });
    return JSON.stringify(formObj, null, 2);
  }

  // AppConfig.current().appId
  // file.documentId == '12' ? '34' : '27'
  const appId = AppConfig.current()?.appId;

  const currentAgentId = useMemo(() => {
    if (appId == '1616359454' && file?.type == LibraryItemType.custom) {
      if (R.toString(file?.documentId ?? 0) == '12') {
        return '34';
      } else {
        return '27'; // Default agent ID
      }
    } else {
      return '27'; // Default agent ID
      // return '1';
    }
  }, [file, appId]);

  if (!file) return null;

  return (
    <ModalDrawer maxWidth="xl">
      <ModalHeader customCss={styles.title} titleStyle={{ flex: 1 }}>
        <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormText singleLine toolbar={false} control={titleControl} />
          </div>
          <div className="d-flex">
            <AssistantButton
              clasName="mr-2"
              assistantId={currentAgentId}
              onInitialMessage={m =>
                m
                  .replace('[FORM_DATA]', JSON.stringify(formValue))
                  .replace('[TITLE]', file.name)
                  .replace('[DOCUMENT]', documentDataExplain)
                  .replace('[DOCUMENT_TYPE]', file.documentType)
              }
              onOutputMessage={m => m.replace('[FORM_FORMAT]', buildFormFormatFromFields(file.fields))}
              onDone={onAIOutput}
            >
              Generate with AI
            </AssistantButton>

            {/* {file.documentType == 'SOP' && (
              <AssistantButton
                clasName="mr-2"
                assistantId="2"
                onInitialMessage={m =>
                  m.replace('[FORM_DATA]', JSON.stringify(formValue)).replace('[TITLE]', file.name).replace('[DOCUMENT]', documentDataExplain)
                }
                onDone={onAIOutput}
              >
                Generate with AI
              </AssistantButton>
            )} */}

            <div className="mr-2" style={{ width: 400 }}>
              <FormSelect
                withoutGroup
                control={statusControl}
                items={CommonService.selectItems([LibraryItemStatus.private, LibraryItemStatus.draft, LibraryItemStatus.public], c =>
                  LibraryService.getStatusLabel(c),
                )}
              />
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalContent formContainer={{ onSubmit: form.submit }} customCss={styles.content}>
        {(file.fields || []).map((field, index) => getFieldJSX(field, index, form.controls[`${index}`]))}
      </ModalContent>
      <ModalFooter>
        {autosaved && (
          <Alert color="success" role="alert" className="mb-0" fade style={{ padding: 5 }}>
            <i className="fa fa-save mr-2"></i>
            Document saved
          </Alert>
        )}
        {!!(file.fields || []).length && <ModalAction intent="primary" text={t`Save`} loading={submitting} autoClose={false} onClick={form.submit} />}
      </ModalFooter>
    </ModalDrawer>
  );
});

export const FormFieldsPreview = memo((props: { fields: FCFormDataType[]; values: any[] }) => {
  const { fields, values } = props;

  const FieldPreview = memo((props: { field: FCFormDataType; value: any; id: number }) => {
    const { field, value, id } = props;
    if (field.type === FCType.text || field.type === FCType.email || field.type === FCType.phone || field.type === FCType.paragraph) {
      const f = field as FCTextFormData;
      return (
        <TextBlock
          classNameTitle="font-weight-bold font-size-20"
          className="mb-4"
          title={f.title}
          description={f.description}
          id={id}
          div
          fontSize={16}
        >
          <HTMLErrorBoundary>{HTMLService.toReact(value || '', undefined, false)} </HTMLErrorBoundary>
        </TextBlock>
      );
    } else if (field.type == FCType.date) {
      const f = field as FCDateFormData;
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title} div fontSize={16}>
          {value ? toMoment(value).format(DATE_FORMAT) : ''}
        </TextBlock>
      );
    } else if (field.type === FCType.checkbox) {
      const f = field as FCCheckboxFormData;
      const options = f.options.split('\n');
      const selectedOptions = (value || []).map((v: string) => options.find(o => o === v));
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title} div fontSize={16}>
          {selectedOptions.join(', ')}
        </TextBlock>
      );
    } else if (field.type === FCType.dropdown) {
      const f = field as FCDropdownFormData;
      const options = f.options.split('\n');
      const selectedOption = options.find(o => o === value);
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title} div fontSize={16}>
          {selectedOption}
        </TextBlock>
      );
    } else if (field.type === FCType.upload) {
      const f = field as FCUploadFormData;
      const fileName = R.get(value, 'fileName');
      const url = R.get(value, 'fileUrl');
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title} div fontSize={16}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {fileName || ''}
          </a>
        </TextBlock>
      );
    } else if (field.type === FCType.radio) {
      const f = field as FCRadioFormData;
      const options = f.options.split('\n');
      const selectedOption = options.find(o => o === value);
      return (
        <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={f.title} div fontSize={16}>
          {selectedOption}
        </TextBlock>
      );
    } else if (field.type === FCType.desciption) {
      const f = field as FCDescriptionFormData;
      return <HTMLErrorBoundary>{HTMLService.toReact(f.description || '')}</HTMLErrorBoundary>;
    } else if (field.type === FCType.title) {
      const f = field as FCTitleFormData;
      return (
        <div className="mt-4">
          <CardTitle>{f.title}</CardTitle>
          <hr />
        </div>
      );
    } else if (field.type == FCType.process) {
      const value = (props.value as ProcessFormData[]) || [];

      return (
        <div>
          <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-4" title={`Process Steps`} div fontSize={16}>
            <div></div>
          </TextBlock>
          <SRLWrapper>
            {value.map((v, index) => {
              const isTextOnly = v.type === 'text';
              const firstColumnWidth = isTextOnly ? '100%' : '50%';
              const isVideo = v.type == 'video';

              return (
                <Table key={index} className="table table-centered table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: firstColumnWidth }}>Step {index + 1}</th>
                      {!isTextOnly && <th style={{ width: '50%' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={index}>
                      {!isTextOnly && (
                        <td style={{ width: '50%' }}>
                          {v.type === 'image' && <img className="img-fluid" src={v.image} />}
                          {isVideo && (v.embedUrl || '').includes('iframe') && (
                            <div style={{ width: '100%', height: 400 }}>
                              <HTMLErrorBoundary>{HTMLService.toReact(v.embedUrl)}</HTMLErrorBoundary>
                            </div>
                          )}
                          {isVideo && !R.isEmpty(v.videoUrl) && (
                            <>
                              {(MediaService.isYoutubeLink(v.videoUrl) || MediaService.isVimeoLink(v.videoUrl)) && (
                                <ReactPlayer url={`${v.videoUrl}`} controls={true} width="100%" height="500px" />
                              )}
                            </>
                          )}
                        </td>
                      )}
                      <td style={{ width: firstColumnWidth, display: 'flex', flexDirection: 'column', whiteSpace: 'initial' }}>
                        <HTMLErrorBoundary>{HTMLService.toReact(v.content || '')}</HTMLErrorBoundary>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              );
            })}
          </SRLWrapper>
        </div>
      );
    } else if (field.type === FCType.grid) {
      const value = (props.value as any) || { rows: [] };
      const field = props.field as FCGridFormData;
      return (
        <div className="mb-4">
          <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-1" title={field.title} div fontSize={16}>
            <div></div>
          </TextBlock>
          <div className="table-responsive">
            <Table className="table" bordered>
              {value.rows.map((row, i) => (
                <tr key={`row-${i + 1}`} className="mx-0">
                  {row.cols.map((ctrl, index) => (
                    <td className="p-2 align-middle" key={`${i + 1}-column-${index}`} style={{ minWidth: '150px' }}>
                      {ctrl.title || <br />}
                    </td>
                  ))}
                </tr>
              ))}
            </Table>
          </div>
        </div>
      );
    } else if (field.type === FCType.repeater) {
      const value = props.value as any[][];
      const field = props.field as FCRepeaterFormData;
      return (
        <div className="mb-4">
          {value.map((item, index) =>
            item.map((val, i) => {
              return <FieldPreview key={index} field={field.items[i]} value={val} id={index + i} />;
            }),
          )}
        </div>
      );
    } else if (field.type === FCType.function) {
      const value = props.value as any[];
      const field = props.field as any;
      return (
        <div className="mb-3">
          <TextBlock title="Function Definitions" div fontSize={16}>
            {[JobAuthorify.getApproval, JobAuthorify.handle, JobAuthorify.act].map((act, index) => (
              <div key={index} className="d-flex mb-1">
                <div className="ml-1">{index + 1} - </div>
                <div>{JOB_DESCRIPTION_AUTHORITIES[act]}</div>
              </div>
            ))}
          </TextBlock>
          <div className="mb-4"></div>
          <Label className="mb-3 font-weight-bold">{field.title}</Label>
          {value.map((f, index) => (
            <div className="ml-2 d-flex" key={index}>
              <div className="flex-fill" style={{ fontSize: '16px' }}>
                ({[JobAuthorify.getApproval, JobAuthorify.handle, JobAuthorify.act].indexOf(f.authorityLevel) + 1}) {f.function}
                <hr />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <div></div>;
  });

  return (
    <div>
      {fields.map((field, index) => (
        <FieldPreview key={index} field={field} value={values[index]} id={index} />
      ))}
    </div>
  );
});
